import { useEffect, type CSSProperties } from 'react'
import { ArrowRight, Globe } from 'lucide-react'
import '@fontsource/golos-text/700.css'
import '@fontsource/golos-text/800.css'
import '@fontsource/golos-text/900.css'
import { AppleIcon, WindowsIcon } from '@/components/OsIcons'
import { DesktopTitlebar } from '@/components/DesktopTitlebar'
import { BotCode, HeroStage, ImportDemo, MusicDemo, ServerTiles, StreamDemo, VoiceDemo } from '@/components/landing/LandingDemos'
import { DOWNLOADS, browserDownload, isDesktop } from '@/lib/desktop'
import { cx } from '@/lib/cx'
import { trackMarketingPage, trackAppDownload } from '@/lib/metrika'
import { LINKS } from '@/lib/links'
import styles from './LandingPage.module.css'

/**
 * Палитра главной — одна тёмная тема, как в рекламе. Переменные читают LandingPage.module.css
 * и иллюстрации (LandingDemos.module.css); ставятся на корень страницы.
 */
const PALETTE = {
  '--l-ink': '#0b0c11',
  '--l-panel': '#13141b',
  '--l-panel-2': '#1a1b23',
  '--l-line': 'rgba(255, 255, 255, 0.08)',
  '--l-text': '#f2f3f8',
  '--l-soft': '#c5c8d6',
  '--l-muted': '#a0a3b5',
  '--l-dim': '#7c7f92',
  '--l-accent': '#b4bbff',
  '--l-brand': '#5865f2',
  '--l-live': '#d83a3d',
  '--l-online': '#3ba55c',
  '--l-mono': 'var(--font-code)',
} as CSSProperties

/*
 * Тексты разделов и вопросов лежат данными: из этих же строк seo/home.mjs собирает главную для
 * поисковых роботов. Одна запись — одна строка, строки в одинарных кавычках и без кавычек внутри.
 */
const SECTIONS = {
  streams: { eyebrow: 'Стримы', title: 'До 4K при 60 кадрах', text: 'Качество выбираете перед запуском, звук игры идёт вместе с картинкой. Смотреть можно в окне, на весь экран или «картинкой в картинке».' },
  voice: { eyebrow: 'Голос', title: 'Общение без лимита времени', text: 'Нейросеть RNNoise приглушает вентилятор и клавиатуру. Рация в приложении работает поверх игры: на клавише или боковой кнопке мыши. Громкость каждого собеседника — от 0 до 200 %.' },
  servers: { eyebrow: 'Серверы', title: 'Роли, ветки и модерация', text: 'Сервер устроен как в Discord: категории, каналы с правами, роли, приглашения, автомодерация и журнал аудита.' },
  import: { eyebrow: 'Переезд', title: 'Сервер из Discord за несколько минут', text: 'Свой бот, консоль и токены не нужны: структуру снимает бот Кличата. Сообщения не переносятся.' },
  music: { eyebrow: 'Музыка', title: 'Музыкальный бот уже на сервере', text: 'Кличат Музыка играет в голосовом канале по команде в чате: ссылка или название с YouTube, очередь, громкость.' },
  bots: { eyebrow: 'Боты и API', title: 'Боты Discord без переписывания', text: 'API повторяет Discord: бот на discord.js или discord.py подключается сменой адреса.' },
}

const FAQ = [
  { q: 'Это бесплатно?', a: 'Да, всё бесплатно: общение, серверы, стримы и боты. Плюс и бусты тоже бесплатные — их дают за приглашённых друзей.' },
  { q: 'Нужен ли VPN?', a: 'Нет. Кличат работает напрямую — в браузере и в приложениях для Windows и macOS.' },
  { q: 'Переносятся ли сообщения из Discord?', a: 'Нет, переносится структура: каналы, роли, права, ветки, эмодзи. Роли участники получают по кодам переноса.' },
  { q: 'Есть приложение для телефона?', a: 'Пока нет. На телефоне Кличат открывается в браузере — с голосом и звонками.' },
  { q: 'Как подключить своего бота?', a: 'Создайте приложение в разделе «Настройки → Разработчикам» и укажите адрес API Кличата в discord.js или discord.py.' },
  { q: 'Что-то не работает?', a: 'Напишите в поддержку в Telegram или на hello@klichat.ru — отвечает живой человек.' },
]

const FACTS = [
  { value: '0 ₽', label: 'общение, серверы, стримы и боты' },
  { value: 'Без лимита', label: 'время общения' },
  { value: '4K · 60 к/с', label: 'стримы на серверах 3-го уровня' },
  { value: 'API Discord', label: 'боты на discord.js и discord.py без переписывания' },
]

/** Качество стрима по уровню сервера (web/src/lib/screenShare.ts, shareCaps). */
const STREAM_TIERS = [
  { quality: '1080p · 30 к/с', where: 'любой сервер' },
  { quality: '1080p · 60 к/с', where: 'сервер 1-го уровня или Плюс' },
  { quality: '1440p · 60 к/с', where: 'сервер 2-го уровня' },
  { quality: '4K · 60 к/с', where: 'сервер 3-го уровня' },
]

const VOICE_SPECS = [
  { name: 'RNNoise', what: 'шумоподавление на нейросети' },
  { name: 'Рация', what: 'клавиша или кнопка мыши' },
  { name: '384 кбит/с', what: 'голос на серверах 3-го уровня' },
]

const IMPORT_STEPS = [
  { title: 'Войдите через Discord', text: 'в настройках сервера, раздел «Импорт из Discord».' },
  { title: 'Проверьте предпросмотр', text: 'и запустите импорт.' },
  { title: 'Раздайте коды переноса', text: '— по ним участники получат свои роли.' },
]

function scrollToId(id: string) {
  const smooth = typeof matchMedia === 'undefined' || !matchMedia('(prefers-reduced-motion: reduce)').matches
  document.getElementById(id)?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
}

/** Надзаголовок, заголовок и абзац раздела; compact — для двух карточек в ряд. */
function Heading({ section, compact = false }: { section: { eyebrow: string; title: string; text: string }; compact?: boolean }) {
  return (
    <>
      <p className={styles.eyebrow}>{section.eyebrow}</p>
      <h2 className={compact ? styles.pairTitle : styles.title}>{section.title}</h2>
      <p className={compact ? styles.pairText : styles.text}>{section.text}</p>
    </>
  )
}

/** Главная для тех, кто ещё не вошёл: что это, чем лучше, кнопки входа и приложения. */
export function LandingPage({ onLogin, onRegister, onCatalog }: { onLogin: () => void; onRegister: () => void; onCatalog: () => void }) {
  useEffect(() => { trackMarketingPage('/') }, [])
  // в приложении скачивать нечего, в браузере предлагаем версию под систему, на телефоне и Linux — ничего
  const download = browserDownload()
  const DownloadIcon = download?.url === DOWNLOADS.windows ? WindowsIcon : AppleIcon

  const actions = (
    <div className={styles.actions}>
      <button type="button" className={styles.primary} onClick={onRegister}>{isDesktop ? 'Создать аккаунт' : 'Открыть в браузере'}</button>
      {isDesktop ? (
        <button type="button" className={styles.secondary} onClick={onLogin}>Войти</button>
      ) : (
        download && (
          <a className={styles.secondary} href={download.url} onClick={() => trackAppDownload(download.url)}>
            <DownloadIcon size={18} />
            <span>{download.label}</span>
          </a>
        )
      )}
      {!isDesktop && (
        <button type="button" className={styles.more} onClick={() => scrollToId('download')}>
          {download ? 'Все версии' : 'Приложения для Windows и macOS'}
        </button>
      )}
    </div>
  )

  return (
    <div className={styles.page} style={PALETTE}>
      <DesktopTitlebar />
      <div className={styles.glowTop} aria-hidden="true" />
      <div className={styles.glowSide} aria-hidden="true" />

      <header className={cx(styles.wrap, styles.top)}>
        <img className={styles.logo} src="/brand/wordmark-light.png" alt="Кличат" width={109} height={35} />
        <nav className={styles.nav} aria-label="Разделы">
          <button type="button" onClick={() => scrollToId('features')}>Возможности</button>
          <button type="button" onClick={onCatalog}>Каталог серверов</button>
          <button type="button" onClick={() => scrollToId('bots')}>Боты и API</button>
          {!isDesktop && <button type="button" onClick={() => scrollToId('download')}>Скачать</button>}
        </nav>
        <div className={styles.account}>
          <button type="button" className={styles.login} onClick={onLogin}>Войти</button>
          <button type="button" className={styles.open} onClick={onRegister}>{isDesktop ? 'Создать аккаунт' : 'Открыть Кличат'}</button>
        </div>
      </header>

      <section className={cx(styles.wrap, styles.hero)}>
        <button type="button" className={styles.news} onClick={() => scrollToId('features')}>
          <span className={styles.newsTag}>Новое</span>
          <span>Переработанные стримы и рация на кнопках мыши</span>
          <ArrowRight size={16} aria-hidden="true" />
        </button>
        <div className={styles.headline}>
          <h1 className={styles.h1}>Общение <span className={styles.accent}>без запретов.</span></h1>
          <p className={styles.lead}>Серверы с ролями, голос без лимита времени и стримы до 4K при 60 кадрах. Работает без VPN: в браузере, на Windows и macOS.</p>
        </div>
        {actions}
        <p className={styles.fine}>Бесплатно · регистрация за минуту</p>
      </section>

      <div className={cx(styles.wrap, styles.stage)}>
        <HeroStage />
      </div>

      <section className={cx(styles.wrap, styles.facts)} aria-label="Коротко">
        {FACTS.map((f) => (
          <div key={f.value} className={styles.fact}>
            <p className={styles.factValue}>{f.value}</p>
            <p className={styles.factLabel}>{f.label}</p>
          </div>
        ))}
      </section>

      <section id="features" className={cx(styles.wrap, styles.split)}>
        <div className={styles.copy}>
          <Heading section={SECTIONS.streams} />
          <ul className={styles.specs}>
            {STREAM_TIERS.map((t, i) => (
              <li key={t.quality} className={cx(i === STREAM_TIERS.length - 1 && styles.specTop)}>
                <b>{t.quality}</b>
                <span>{t.where}</span>
              </li>
            ))}
          </ul>
          <p className={styles.note}>Уровень сервера поднимают бусты. Их, как и Плюс, дают бесплатно — за приглашённых друзей.</p>
        </div>
        <div className={styles.visual}>
          <StreamDemo />
        </div>
      </section>

      <section className={cx(styles.wrap, styles.split, styles.splitReverse)}>
        <div className={styles.copy}>
          <Heading section={SECTIONS.voice} />
          <ul className={styles.specs}>
            {VOICE_SPECS.map((s, i) => (
              <li key={s.name} className={cx(i === VOICE_SPECS.length - 1 && styles.specTop)}>
                <b>{s.name}</b>
                <span>{s.what}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.visual}>
          <VoiceDemo />
        </div>
      </section>

      <section id="servers" className={styles.wrap}>
        <div className={styles.serversHead}>
          <div>
            <p className={styles.eyebrow}>{SECTIONS.servers.eyebrow}</p>
            <h2 className={styles.title}>{SECTIONS.servers.title}</h2>
          </div>
          <p className={styles.text}>{SECTIONS.servers.text}</p>
        </div>
        <ServerTiles onCatalog={onCatalog} />
      </section>

      <section className={cx(styles.wrap, styles.split, styles.splitWide)}>
        <div className={styles.copy}>
          <Heading section={SECTIONS.import} />
          <ol className={styles.steps}>
            {IMPORT_STEPS.map((s, i) => (
              <li key={s.title}>
                <span className={styles.stepNo}>{i + 1}</span>
                <span><b>{s.title}</b> {s.text}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className={styles.visual}>
          <ImportDemo />
        </div>
      </section>

      <section id="bots" className={cx(styles.wrap, styles.pair)}>
        <article className={styles.pairCard}>
          <Heading section={SECTIONS.music} compact />
          <MusicDemo />
        </article>
        <article className={styles.pairCard}>
          <Heading section={SECTIONS.bots} compact />
          <BotCode />
          <a className={styles.docsLink} href="/docs">
            Документация API
            <ArrowRight size={16} aria-hidden="true" />
          </a>
        </article>
      </section>

      <section className={cx(styles.wrap, styles.faq)}>
        <div>
          <p className={styles.eyebrow}>Вопросы</p>
          <h2 className={styles.title}>Частые вопросы</h2>
          <p className={styles.text}>
            Нет ответа — напишите в поддержку в Telegram: <a href={LINKS.support} target="_blank" rel="noopener">@klichat_support</a>
          </p>
        </div>
        <dl className={styles.faqList}>
          {FAQ.map((f) => (
            <div key={f.q} className={styles.faqItem}>
              <dt>{f.q}</dt>
              <dd>{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section id="download" className={styles.finale}>
        <div className={styles.finaleGlow} aria-hidden="true" />
        <div className={cx(styles.wrap, styles.finaleInner)}>
          <h2 className={styles.finaleTitle}>Бросьте <span className={styles.accent}>клич.</span></h2>
          <p className={styles.finaleText}>Создайте сервер и отправьте друзьям приглашение: ссылка открывается и в браузере, и в приложении.</p>
          {actions}
          {!isDesktop && (
            <>
              <ul className={styles.platforms} aria-label="Приложения">
                <li>
                  <a className={styles.platform} href={DOWNLOADS.windows} onClick={() => trackAppDownload(DOWNLOADS.windows)}>
                    <WindowsIcon size={26} />
                    <span><b>Windows</b><small>10 и 11 · установщик</small></span>
                  </a>
                </li>
                <li>
                  <a className={styles.platform} href={DOWNLOADS.mac} onClick={() => trackAppDownload(DOWNLOADS.mac)}>
                    <AppleIcon size={26} />
                    <span><b>macOS</b><small>Apple Silicon · dmg</small></span>
                  </a>
                </li>
                <li>
                  <a className={styles.platform} href={DOWNLOADS.macIntel} onClick={() => trackAppDownload(DOWNLOADS.macIntel)}>
                    <AppleIcon size={26} />
                    <span><b>macOS</b><small>Intel · dmg</small></span>
                  </a>
                </li>
                <li>
                  <button type="button" className={styles.platform} onClick={onRegister}>
                    <Globe size={26} aria-hidden="true" />
                    <span><b>Браузер</b><small>без установки</small></span>
                  </button>
                </li>
              </ul>
              <p className={styles.fine}>
                В приложении есть рация поверх игр, выбор окна для стрима, трей и уведомления. Подписи издателя пока нет: при первом запуске система покажет предупреждение.
              </p>
            </>
          )}
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={cx(styles.wrap, styles.footerInner)}>
          <div className={styles.footBrand}>
            <img src="/brand/wordmark-light.png" alt="Кличат" width={93} height={30} />
            <p>Голосовые каналы, серверы и стримы для своих.</p>
            <p className={styles.legal}>
              <span>© {new Date().getFullYear()} Кличат</span>
              <a href="/legal/terms">Соглашение</a>
              <a href="/legal/privacy">Конфиденциальность</a>
            </p>
          </div>
          {/* страницы для поисковиков (web/seo/pages): по ссылкам с главной их находят и роботы */}
          <nav className={styles.footCol} aria-label="Продукт">
            <p className={styles.footTitle}>Продукт</p>
            <button type="button" onClick={() => scrollToId('features')}>Возможности</button>
            <button type="button" onClick={onCatalog}>Каталог серверов</button>
            <a href="/strimy-4k">Стримы 4K</a>
            <a href="/gaidy/plus-za-druzej">Плюс за друзей</a>
          </nav>
          <nav className={styles.footCol} aria-label="Сообществам">
            <p className={styles.footTitle}>Сообществам</p>
            <a href="/perenos-iz-discord">Перенос из Discord</a>
            <a href="/analog-discord">Аналог Discord</a>
            <a href="/igry">Для игр</a>
            <a href="/gaidy">Гайды</a>
            <a href={LINKS.officialServer}>Официальный сервер</a>
          </nav>
          <nav className={styles.footCol} aria-label="Разработчикам">
            <p className={styles.footTitle}>Разработчикам</p>
            <a href="/boty">Боты и API</a>
            <a href="/docs">Документация</a>
          </nav>
          <nav className={styles.footCol} aria-label="Связь">
            <p className={styles.footTitle}>Связь</p>
            <a href={LINKS.telegramChannel} target="_blank" rel="noopener">Канал в Telegram</a>
            <a href={LINKS.support} target="_blank" rel="noopener">Поддержка</a>
            <a href={`mailto:${LINKS.supportEmail}`}>{LINKS.supportEmail}</a>
          </nav>
        </div>
      </footer>
    </div>
  )
}
